"use client";
import { Image, Typography, Avatar, Space } from "antd";
import { HeartFilled, HeartOutlined } from "@ant-design/icons";
import { track } from "@vercel/analytics/react";
import Link from "next/link";
import React, { useCallback, useMemo, useState, useEffect } from "react";
import { getLargeSrc, getPublicSrc, getPhotoHeight } from "@/lib/helpers";
import dynamic from "next/dynamic";
import LazyVideo from "@/app/components/LazyVideo";
import {
  enableMutedAutoplay,
  handleVideoError,
  VIDEO_PLAYER_FILE_CONFIG,
} from "@/lib/video";
import type { UsePhotoLikesResult } from "../hooks/usePhotoLikes";

const ReactPlayer = dynamic(() => import("react-player"), { ssr: false });

// React 19 SSR emits a `<link rel="preload" as="image">` for every non-lazy
// <img>, so a 24-photo page preloads the whole grid up front. Keep only the
// first rows eager (grid tops out at 5 columns) and lazy-load the rest.
const EAGER_COUNT = 8;

import type { PhotoEngagement } from "@/lib/api/photoEngagement";
import { usePhotoEngagement } from "@/lib/hooks/usePhotoEngagement";

interface IPhoto extends PhotoEngagement {
  _id: string;
  src: string; // base URL that ends with a variant segment (e.g., .../public or .../large)
  alt: string;
  type: "image" | "video";
  user: {
    _id: string;
    name: string;
    username: string;
    photo: string;
  };
  service?: { title: string; category: number };
}

export default function PhotoGrid({
  photos,
  grid,
  changeUrl,
  likes,
  trackingEnabled = true,
}: {
  photos: IPhoto[];
  grid: "AUTO" | "SINGLE" | "MULTIPLE";
  changeUrl?: boolean;
  likes?: UsePhotoLikesResult;
  trackingEnabled?: boolean;
}) {
  const [photoId, setPhotoId] = useState<string | null>(null);

  const { root, trackOpen } = usePhotoEngagement(trackingEnabled);
  const selectedPhoto = photos.find((photo) => photo._id === photoId);
  useEffect(() => {
    if (selectedPhoto) trackOpen(selectedPhoto);
  }, [selectedPhoto, trackOpen]);

  // Read photoId from URL on mount
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const id = params.get("photoId");
    if (id) setPhotoId(id);
  }, []);

  const onChangePreview = useCallback(
    (
      pid: string,
      trackData: {
        photoId: string;
        ownerName: string;
        ownerId: string;
        type: "CLICK" | "VIEW";
      },
    ) => {
      if (changeUrl && typeof window !== "undefined") {
        const url = new URL(window.location.href);
        url.searchParams.set("photoId", pid);
        window.history.replaceState(null, "", url.toString());
      }
      setPhotoId(pid);
      track("PhotoView", trackData);
    },
    [changeUrl],
  );

  const onClosePreview = useCallback(() => {
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("photoId");
      window.history.replaceState(null, "", url.toString());
    }
    setPhotoId(null);
  }, []);

  const renderLikeButton = useCallback(
    (photo: IPhoto) => {
      if (!likes) return null;
      const liked = likes.isLiked(photo._id);
      const count = likes.getLikeCount(photo._id);
      const pending = likes.isPending(photo._id);

      return (
        <button
          type="button"
          className={`photo-like-button${liked ? " is-liked" : ""}`}
          aria-pressed={liked}
          aria-label={liked ? "Unlike photo" : "Like photo"}
          disabled={!likes.ready || pending}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            likes.toggleLike(photo._id);
            track("PhotoLike", {
              photoId: photo._id,
              ownerId: photo.user._id,
              liked: !liked,
            });
          }}
        >
          {liked ? <HeartFilled /> : <HeartOutlined />}
          {count > 0 && <span className="photo-like-count">{count}</span>}
        </button>
      );
    },
    [likes],
  );

  // Preview uses LARGE variant; grid uses PUBLIC
  const previewItems = useMemo(
    () =>
      photos.map((p) => {
        const src = p.type === "video" ? p.src : getLargeSrc(p.src);
        return {
          ...p,
          src, // use LARGE for preview
        };
      }),
    [photos],
  );

  const renderMedia = useCallback(
    (photo: IPhoto, index: number) => {
      const loading = index < EAGER_COUNT ? undefined : ("lazy" as const);
      const handleClick = () =>
        onChangePreview(photo._id, {
          photoId: photo._id,
          ownerName: photo.user.name,
          ownerId: photo.user._id,
          type: "CLICK",
        });

      if (photo.type === "video") {
        return (
          <button
            type="button"
            data-work-photo-id={photo.workPhotoId}
            data-salon-id={photo.salonId}
            className="react-player-wrapper photo-media-button"
            style={
              grid === "AUTO" ? undefined : { height: getPhotoHeight(grid) }
            }
            onClick={handleClick}
            aria-label={`View ${photo.alt || `video by ${photo.user.name}`}`}
          >
            <LazyVideo
              url={photo.src}
              width="100%"
              height="100%"
              posterLoading={loading}
              reducedMotionControls={false}
            />
            <div className="react-player-mask" />
          </button>
        );
      }

      return (
        <button
          type="button"
          data-work-photo-id={photo.workPhotoId}
          data-salon-id={photo.salonId}
          className="photo-media-button"
          onClick={handleClick}
          aria-label={`View ${photo.alt || `photo by ${photo.user.name}`}`}
        >
          <Image
            src={getPublicSrc(photo.src)}
            alt={photo.alt}
            width="100%"
            height={grid === "AUTO" ? undefined : getPhotoHeight(grid)}
            preview={false}
            loading={loading}
          />
        </button>
      );
    },
    [grid, onChangePreview],
  );

  return (
    <Image.PreviewGroup
      items={previewItems}
      preview={{
        open: !!selectedPhoto,
        current: photoId
          ? Math.max(
              0,
              photos.findIndex((p) => p._id === photoId),
            )
          : 0,
        actionsRender: () => {
          const currentIndex = photoId
            ? Math.max(
                0,
                photos.findIndex((p) => p._id === photoId),
              )
            : 0;
          const currentPhoto = photos[currentIndex];
          if (!currentPhoto || !changeUrl) return null;
          const attribution = (
            <Space>
              <Typography.Text className="has-text-white">
                Work by
              </Typography.Text>
              <Avatar src={currentPhoto.user.photo} />
              <Typography.Text strong className="has-text-white">
                {currentPhoto.user.name}
              </Typography.Text>
            </Space>
          );
          return currentPhoto.user.username ? (
            <Link
              href={`/stylists/${currentPhoto.user.username}`}
              className="has-text-white"
            >
              {attribution}
            </Link>
          ) : (
            <div className="has-text-white">{attribution}</div>
          );
        },
        imageRender: (originNode, { current }) => {
          const currentPhoto = previewItems[current];
          if (currentPhoto?.type === "video") {
            return (
              <ReactPlayer
                url={currentPhoto.src}
                playing
                controls={false}
                muted
                loop
                width="100%"
                height="100%"
                config={{
                  file: VIDEO_PLAYER_FILE_CONFIG,
                }}
                onReady={(player) => {
                  enableMutedAutoplay(player);
                }}
                onError={handleVideoError}
              />
            );
          }
          return originNode;
        },
        onChange: (current) => {
          const p = photos[current];
          if (!p) return;
          onChangePreview(p._id, {
            photoId: p._id,
            ownerName: p.user.name,
            ownerId: p.user._id,
            type: "VIEW",
          });
        },
        onOpenChange: (open) => {
          if (!open) onClosePreview();
        },
      }}
    >
      <div
        ref={root}
        className={`photo-grid ${
          grid === "SINGLE"
            ? "photo-grid--single"
            : grid === "MULTIPLE"
              ? "photo-grid--multiple"
              : "photo-grid--auto"
        }`}
      >
        {photos.map((photo, index) => (
          <div
            key={photo._id}
            className="photo-grid-item"
            style={{ position: "relative" }}
          >
            {renderMedia(photo, index)}
            {renderLikeButton(photo)}
            {changeUrl && (
              <div className="has-text-right">
                {photo.user.username ? (
                  <Link href={`/stylists/${photo.user.username}`}>
                    <Space size={8} align="center">
                      <Avatar
                        src={photo.user.photo}
                        alt={photo.user.name}
                        size={18}
                      >
                        {photo.user.name?.charAt(0)}
                      </Avatar>
                      <Typography.Text style={{ fontSize: 13 }}>
                        {photo.user.name}
                      </Typography.Text>
                    </Space>
                  </Link>
                ) : (
                  <Space size={8} align="center">
                    <Avatar
                      src={photo.user.photo}
                      alt={photo.user.name}
                      size={18}
                    >
                      {photo.user.name?.charAt(0)}
                    </Avatar>
                    <Typography.Text style={{ fontSize: 13 }}>
                      {photo.user.name}
                    </Typography.Text>
                  </Space>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </Image.PreviewGroup>
  );
}
